import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface UseSpeechRecognitionOptions {
  /** BCP-47 language tag. Defaults to current i18n locale (fr-FR / en-US). */
  lang?: string;
  /** Keep listening after each utterance. Default: false (one-shot). */
  continuous?: boolean;
  /** Emit interim (partial) results. Default: true. */
  interimResults?: boolean;
  /** Called on each recognition result. */
  onResult?: (text: string, isFinal: boolean) => void;
  /** Called on recognition error. */
  onError?: (error: string) => void;
}

export interface UseSpeechRecognitionReturn {
  isListening: boolean;
  isSupported: boolean;
  transcript: string;
  startListening: () => void;
  stopListening: () => void;
  resetTranscript: () => void;
}

function getSpeechRecognitionClass(): (new () => SpeechRecognition) | null {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

function i18nToSpeechLang(lang: string): string {
  if (lang.startsWith('fr')) return 'fr-FR';
  if (lang.startsWith('en')) return 'en-US';
  return lang;
}

/**
 * Silence timeout (ms). If no new STT results arrive for this duration after
 * the last interim result, treat the accumulated interim text as final.
 * This is critical for iOS Safari where `isFinal` may never become `true`.
 */
const SILENCE_TIMEOUT_MS = 1000;

export function useSpeechRecognition(
  options: UseSpeechRecognitionOptions = {},
): UseSpeechRecognitionReturn {
  const { i18n } = useTranslation();
  const { lang, continuous = false, interimResults = true, onResult, onError } = options;

  const resolvedLang = lang ?? i18nToSpeechLang(i18n.language);
  const isSupported = getSpeechRecognitionClass() !== null;

  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);

  // Keep callback refs fresh without triggering effect re-runs.
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  const startListening = useCallback(() => {
    const SpeechRecognitionClass = getSpeechRecognitionClass();
    if (!SpeechRecognitionClass) return;

    // Stop any existing instance first.
    recognitionRef.current?.abort();

    const recognition = new SpeechRecognitionClass();
    recognition.lang = resolvedLang;
    recognition.continuous = continuous;
    recognition.interimResults = interimResults;

    // iOS isFinal fallback: track last interim text and use a silence timer.
    let silenceTimer: ReturnType<typeof setTimeout> | null = null;
    let lastInterimText = '';

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const alternative = result?.[0];
        if (!result || !alternative) continue;
        if (result.isFinal) {
          finalTranscript += alternative.transcript;
        } else {
          interimTranscript += alternative.transcript;
        }
      }

      if (finalTranscript) {
        // Got a real final result — clear any pending silence timer.
        if (silenceTimer) {
          clearTimeout(silenceTimer);
          silenceTimer = null;
        }
        lastInterimText = '';
        setTranscript(finalTranscript);
        onResultRef.current?.(finalTranscript, true);
      } else if (interimTranscript) {
        lastInterimText = interimTranscript;
        setTranscript(interimTranscript);
        onResultRef.current?.(interimTranscript, false);

        // Reset silence timer — if no new results arrive within SILENCE_TIMEOUT_MS,
        // treat the last interim as final (iOS Safari workaround).
        if (silenceTimer) clearTimeout(silenceTimer);
        silenceTimer = setTimeout(() => {
          if (lastInterimText) {
            onResultRef.current?.(lastInterimText, true);
            lastInterimText = '';
          }
        }, SILENCE_TIMEOUT_MS);
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      // 'no-speech' and 'aborted' are not real errors — just silence or user-stop.
      if (event.error === 'no-speech' || event.error === 'aborted') return;
      onErrorRef.current?.(event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      if (silenceTimer) {
        clearTimeout(silenceTimer);
        silenceTimer = null;
      }
      // If STT ended but we still have pending interim text that was never
      // marked as final, emit it as final now (common on iOS Safari).
      if (lastInterimText) {
        onResultRef.current?.(lastInterimText, true);
        lastInterimText = '';
      }
      setIsListening(false);
    };

    recognition.start();
    recognitionRef.current = recognition;
    setIsListening(true);
  }, [resolvedLang, continuous, interimResults]);

  const resetTranscript = useCallback(() => {
    setTranscript('');
  }, []);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
    };
  }, []);

  return {
    isListening,
    isSupported,
    transcript,
    startListening,
    stopListening,
    resetTranscript,
  };
}
